import { baseApi } from 'services/api';
import { API_BASE_URL, API_TAG_TYPES, API_VERSION } from 'constants/common';
import {
  AutoFillFeedbackPayload,
  AutoFillFeedbackResponse,
  CloseInvoicePayload,
  CloseInvoiceResponse,
  CreateCustomAmountTrackingPayload,
  CreateCustomAmountTrackingResponse,
  CreateInvoiceWithRiskValidationRequest,
  CreateInvoiceWithRiskValidationResponse,
  CustomerByIdPayload,
  CustomerDetails,
  DeleteInvoicePayload,
  DiscountSettings,
  EditInvoiceWithRiskValidationRequest,
  EditInvoiceWithRiskValidationResponse,
  GetCustomersPayload,
  GetCustomersResponse,
  GetInvoiceAttachmentsResponse,
  GetInvoiceCategoryItemListingResponse,
  GetInvoiceCategoryItemListingsPayload,
  GetInvoiceQRImagePayload,
  GetInvoiceQRImageResponse,
  GetInvoiceRefundableTransactionsPayload,
  GetInvoiceRefundableTransactionsResponse,
  GetInvoiceSearchSuggestionPayload,
  GetInvoiceSearchSuggestionResponse,
  GetInvoicesPayload,
  GetInvoicesResponse,
  GetInvoicesV2Payload,
  GetInvoicesV2Response,
  GetMerchantsPayload,
  GetPresignedUrlsPayload,
  ImageUploadResponse,
  InvoiceByIdPayload,
  InvoiceByIdResponseV2,
  InvoiceDownloadPayload,
  InvoiceReminder,
  MarkPaidPayload,
  MarkPaidResponse,
  Merchant,
  ParseInvoicePayload,
  ParseInvoiceResponse,
  PresignedUrlResponse,
  ShareInvoicePayload,
  ShareInvoiceResponse,
  TaxesAndFeesPayload,
  TaxesAndFeesResponse
} from './invoices.model';
import { FileTypes } from 'types/baseTypes';
import { downloadFile, printFile } from 'utils/commonUtils';
import { InvoiceSurchargeState, TaxesAndFeesPayloadState, updateInvoiceFeeSettings } from './InvoicesSlice';
import {
  TaxesAndFeesPayloadStateEditPage,
  initialiseInvoiceEditSliceFromV2,
  updateInvoiceFeeSettingsEditPage
} from './edit-invoice/invoicesEditSlice';
import { updateInvoiceByIdRawDataV2, updateInvoiceData } from '../payments-side-panel/sidePanelSlice';
import { getUrlSearchParams, getUrlSearchParamsWithArrays } from 'utils/apiUtils';
import {
  initialiseJobDetailsFromInvoiceV2,
  updateInvoiceFeeSettingsInJobs
} from 'containers/jobs/create-edit-job/createJobSlice';
import {
  initializeEstimateFromInvoiceV2,
  updateInvoiceFeeSettingsInEstimates,
  updateIsInitialisingEstimateFromAPI
} from 'containers/estimates/estimatesSlice';
import { TipTimingEnum } from './constants';
import { getIsSignatureEnabledState } from './utils';
import { initializeInvoiceFromAutofill } from './InvoicesSlice';

const InvoicesBaseApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    getCategoryItemList: builder.query<GetInvoiceCategoryItemListingResponse, GetInvoiceCategoryItemListingsPayload>({
      query: params => {
        let url = `${API_VERSION.V1}/catalogue/invoice-listings?`;
        const urlParams = getUrlSearchParams(params);
        url = url + urlParams;
        return {
          url: url,
          method: 'GET'
        };
      }
    }),

    getInvoices: builder.query<GetInvoicesResponse, GetInvoicesPayload>({
      query: params => {
        let url = `${API_BASE_URL.WEB_APP}/${API_VERSION.V1}/invoices?`;
        const urlParams = new URLSearchParams();
        Object.keys(params).map(key => {
          if (Array.isArray(params[key])) {
            params[key].map(item => {
              urlParams.append(key, item);
            });
          } else {
            if (params[key] !== null) {
              urlParams.append(key, params[key]);
            }
          }
        });
        url = url + urlParams;
        return {
          url: url,
          method: 'GET'
        };
      },
      extraOptions: {
        failure: 'failed to fetch invoices data',
        showToast: true
      }
    }),

    getInvoicesV2: builder.query<GetInvoicesV2Response, GetInvoicesV2Payload>({
      query: params => {
        const { teamId, ...queryParams } = params;
        const urlParams = getUrlSearchParams(queryParams);
        const url = `${API_VERSION.V7}/teams/${teamId}/invoices?${urlParams.toString()}`;
        return {
          url: url,
          method: 'GET'
        };
      },
      providesTags: [API_TAG_TYPES.INVOICES_LIST],
      extraOptions: {
        failure: 'failed to fetch invoices data',
        showToast: true
      }
    }),

    exportInvoices: builder.query<void, GetInvoicesV2Payload>({
      query: params => {
        const { teamId, ...queryParams } = params;
        const urlParams = getUrlSearchParamsWithArrays(queryParams);
        const url = `${API_VERSION.V7}/teams/${teamId}/invoices/export?${urlParams.toString()}`;
        return {
          url: url,
          method: 'GET',
          responseHandler: async response => {
            await downloadFile({ response, fileType: FileTypes.CSV, fileName: 'invoices' });
          },
          cache: 'no-cache'
        };
      },
      extraOptions: {
        failure: 'Failed to export invoices',
        success: 'Invoices exported successfully!',
        showToast: true
      }
    }),

    downloadInvoiceList: builder.query<any, GetInvoicesPayload>({
      query: params => {
        let url = `${API_BASE_URL.WEB_APP}/${API_VERSION.V1}/invoices?`;
        const urlParams = new URLSearchParams();
        Object.keys(params).map(key => {
          if (Array.isArray(params[key])) {
            params[key].map(item => {
              urlParams.append(key, item);
            });
          } else {
            if (params[key] !== null) {
              urlParams.append(key, params[key]);
            }
          }
        });
        url = url + urlParams;
        return {
          url: url,
          method: 'GET',
          responseHandler: async response => {
            await downloadFile({ response, fileType: params.type, fileName: 'invoices' });
          },
          // Do not cache responses for pdf's or csv's as it may lead to crashing the browser
          cache: 'no-cache'
        };
      },
      extraOptions: {
        failure: 'failed to download invoice list',
        success: 'Invoices exported successfully!',
        showToast: true
      }
    }),

    downloadInvoiceAsPDF: builder.query<any, InvoiceDownloadPayload>({
      query: ({ invoiceId }) => {
        const url = `${API_VERSION.V7}/public/invoices/${invoiceId}/download`;
        return {
          url: url,
          method: 'GET',
          responseHandler: async response => {
            await downloadFile({ response, fileType: FileTypes.PDF, fileName: 'invoice' });
          },
          // Do not cache responses for pdf's or csv's as it may lead to crashing the browser
          cache: 'no-cache'
        };
      },
      extraOptions: {
        failure: 'failed to download invoice pdf',
        success: 'Invoice exported successfully!',
        showToast: true
      }
    }),

    downloadPrintInvoiceAsPDF: builder.query<any, InvoiceDownloadPayload>({
      query: ({ invoiceId }) => {
        const url = `${API_VERSION.V7}/public/invoices/${invoiceId}/download`;
        return {
          url: url,
          method: 'GET',
          responseHandler: async response => {
            await printFile({ response });
          },
          // Do not cache responses for pdf's or csv's as it may lead to crashing the browser
          cache: 'no-cache'
        };
      },
      extraOptions: {
        failure: 'failed to download invoice pdf',
        showToast: true
      }
    }),

    getCustomers: builder.query<GetCustomersResponse, GetCustomersPayload>({
      query: ({ teamId }) => {
        return {
          url: `${API_VERSION.V2}/merchants/customers?teamId=${teamId}`,
          method: 'GET'
        };
      },
      extraOptions: {
        failure: 'failed to fetch customer data'
      }
    }),
    // used in filters
    getAllCustomers: builder.query<GetCustomersResponse, GetCustomersPayload>({
      query: ({ teamId }) => {
        return {
          url: `${API_VERSION.V3}/merchants/customers?teamId=${teamId}`,
          method: 'GET'
        };
      },
      extraOptions: {
        failure: 'failed to fetch customer data'
      }
    }),
    getInvoiceSearchSuggestions: builder.query<GetInvoiceSearchSuggestionResponse, GetInvoiceSearchSuggestionPayload>({
      query: ({ teamId }) => {
        return {
          url: `${API_BASE_URL.WEB_APP}/${API_VERSION.V1}/invoices/search-suggestion?teamId=${teamId}`,
          method: 'GET'
        };
      }
    }),

    shareInvoice: builder.mutation<ShareInvoiceResponse, ShareInvoicePayload>({
      query: ({ teamId, invoiceId, ...payload }) => {
        return {
          url: `${API_VERSION.V7}/teams/${teamId}/invoices/${invoiceId}/share`,
          method: 'POST',
          body: payload
        };
      },
      invalidatesTags: [API_TAG_TYPES.INVOICES_LIST],
      extraOptions: {
        failure: 'Something went wrong, please try again.'
      }
    }),

    createCustomAmountTracking: builder.mutation<CreateCustomAmountTrackingResponse, CreateCustomAmountTrackingPayload>(
      {
        query: ({ invoiceId, requestedAmount, teamId }) => {
          return {
            url: `${API_VERSION.V7}/teams/${teamId}/invoices/${invoiceId}/set-custom-amount`,
            method: 'POST',
            body: { requestedAmount }
          };
        },
        extraOptions: {
          failure: 'Failed to create invoice tracking'
        }
      }
    ),

    markPaid: builder.mutation<MarkPaidResponse, MarkPaidPayload>({
      query: ({ invoiceId, paymentMethod, ...payload }) => {
        return {
          url: `${API_VERSION.V2}/merchants/invoices/${invoiceId}/mark-paid`,
          method: 'POST',
          body: {
            invoiceId,
            PaymentMethod: paymentMethod,
            ...payload
          }
        };
      },
      invalidatesTags: [
        API_TAG_TYPES.JOB_DETAILS,
        API_TAG_TYPES.SUGGESTED_ADJUSTMENTS_FOR_JOB,
        API_TAG_TYPES.CUSTOMERS,
        API_TAG_TYPES.INVOICES_LIST,
        API_TAG_TYPES.GET_PROFITABILITY_SUMMARY
      ]
    }),

    deleteInvoice: builder.mutation<any, DeleteInvoicePayload>({
      query: ({ invoiceId, teamId }) => {
        return {
          url: `${API_VERSION.V1}/merchants/teams/${teamId}/invoices/${invoiceId}`,
          method: 'DELETE'
        };
      },
      invalidatesTags: [
        API_TAG_TYPES.CUSTOMERS,
        API_TAG_TYPES.INVOICES_LIST,
        API_TAG_TYPES.GET_PROFITABILITY_SUMMARY,
        API_TAG_TYPES.JOB_DETAILS
      ]
    }),
    getInvoiceByIdV2: builder.query<InvoiceByIdResponseV2, InvoiceByIdPayload>({
      query: params => {
        let url = `${API_VERSION.V7}/teams/${params.teamId}/invoices/${params.invoiceId}`;
        if (params.timezone) {
          url += `?timezone=${params.timezone}`;
        }
        return {
          url: url,
          method: 'GET'
        };
      },

      async onQueryStarted(params, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        const { isFromEdit = false, isFromJob = false, isFromEstimate = false } = params;

        // Store raw V2 response
        dispatch(updateInvoiceByIdRawDataV2(data));

        if (isFromEstimate) {
          dispatch(updateIsInitialisingEstimateFromAPI(true));
          dispatch(initializeEstimateFromInvoiceV2(data));
        }

        if (isFromEdit) {
          dispatch(initialiseInvoiceEditSliceFromV2(data));
        }

        if (isFromJob) {
          dispatch(initialiseJobDetailsFromInvoiceV2(data));
        }

        //TODO: check the use case for this and remove
        // Transform V2 data to V1 format for updateInvoiceData
        const transformedDataForSidePanel = {
          ...data.invoice,
          customer: data.invoice.invoiceCustomer,
          comment: data.invoice.note,
          hasTip: data.invoice.tip.enabled,
          discountPercentage:
            data.invoice.discount.enabled && data.invoice.discount.type === 'PERCENTAGE'
              ? data.invoice.discount.value.toString()
              : '0',
          discount: data.invoice.discount.enabled ? data.invoice.discount.amount.toString() : '0',
          isDiscountAmountSelected: data.invoice.discount.enabled && data.invoice.discount.type === 'AMOUNT',
          lineItems: data.invoice.lineItems,
          serviceCharges: data.invoice.serviceCharges,
          attachments: data.invoice.attachments,
          paymentModes: data.invoice.paymentMethodList, // Keep as is - no transformation needed
          partialPaymentEnabled: data.invoice.partialPaymentEnabled,
          paymentRemindersEnabled: data.invoice.reminderEnabled,
          paymentReminders: [], // TODO: Transform reminders if available in V2
          tipTiming: data.invoice.tip.type || TipTimingEnum.ALL_PAYMENTS
        };
        dispatch(updateInvoiceData(transformedDataForSidePanel as any));
      },
      providesTags: [API_TAG_TYPES.INVOICE_BY_ID],
      extraOptions: {
        failure: 'Something went wrong'
      }
    }),

    getCustomerById: builder.query<CustomerDetails, CustomerByIdPayload>({
      query: params => {
        const { customerId, teamId } = params;
        return {
          url: `${API_VERSION.V3}/merchants/customers/${customerId}?teamId=${teamId}`,
          method: 'GET'
        };
      },

      extraOptions: {
        failure: 'Failed to fetch customer data'
      }
    }),

    getTaxesAndFees: builder.query<TaxesAndFeesResponse, TaxesAndFeesPayload>({
      query: ({ teamId }) => {
        return {
          url: `${API_BASE_URL.WEB_APP}/${API_VERSION.V1}/teams/taxes-and-fees?teamId=${teamId}`,
          method: 'GET'
        };
      },

      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        const surchargeState: InvoiceSurchargeState = {
          percentage: data?.surcharge?.percentage,
          valueInCurrency: null,
          percentageWithBps: null,
          valueInCurrencyWithBps: null,
          enabled: data?.surcharge?.enabled,
          basePoints: data?.surcharge?.basePoints,
          config: data?.surcharge?.config
        };
        const discountSettings: DiscountSettings = { enabled: data?.discount.enabled };

        dispatch(
          updateInvoiceFeeSettings({
            surchargeState,
            discountSettings
          } as TaxesAndFeesPayloadState)
        );
        dispatch(
          updateInvoiceFeeSettingsEditPage({
            surchargeState,
            discountSettings
          } as TaxesAndFeesPayloadStateEditPage)
        );
        dispatch(
          updateInvoiceFeeSettingsInJobs({
            surchargeState,
            discountSettings
          } as TaxesAndFeesPayloadState)
        );
        dispatch(
          updateInvoiceFeeSettingsInEstimates({
            surchargeState,
            discountSettings
          } as TaxesAndFeesPayloadState)
        );
      },
      extraOptions: {
        failure: 'Failed to get taxes and fees'
      }
    }),
    getInvoicePDFPreview: builder.query<any, { teamId: string; payload: any }>({
      query: ({ teamId, payload }) => {
        const url = `${API_VERSION.V7}/teams/${teamId}/invoices/preview/pdf`;
        return {
          url: url,
          method: 'POST',
          body: payload,
          responseHandler: async response => {
            const blob = await response.blob();
            return blob;
          },
          // Do not cache responses for pdf's or csv's as it may lead to crashing the browser
          cache: 'no-cache'
        };
      },
      extraOptions: {
        failure: 'failed to download invoice preview pdf',
        showToast: true
      }
    }),

    // TODO define payload type
    uploadFile: builder.mutation<ImageUploadResponse, { file: any; teamId: number }>({
      query: ({ teamId, file }) => {
        const formData = new FormData();
        formData.append('teamId', teamId.toString());
        formData.append('image', file);

        return {
          url: `${API_VERSION.V2}/merchants/invoices/upload-images`,
          method: 'POST',
          body: formData,
          headers: {
            // make sure not to set the Content-Type header.
            // The browser will set it for you, including the boundary parameter.
            // 'Content-Type': 'multipart/form-data'
          }
        };
      },

      extraOptions: {
        failure: 'We are unable to upload your image. Try again after sometime.',
        showToast: true
      }
    }),

    getPresignedUrls: builder.mutation<PresignedUrlResponse, GetPresignedUrlsPayload>({
      query: ({ teamId, case: caseValue, attachments }) => {
        return {
          url: `${API_VERSION.V1}/teams/${teamId}/presigned-urls`,
          method: 'POST',
          body: {
            case: caseValue,
            attachments
          }
        };
      },
      extraOptions: {
        failure: 'uploading attachments failed',
        showToast: false
      }
    }),
    parseInvoice: builder.mutation<ParseInvoiceResponse, { teamId: string; body: ParseInvoicePayload }>({
      query: ({ teamId, body }) => {
        return {
          url: `${API_VERSION.V1}/teams/${teamId}/invoices/auto-fill/parse`,
          method: 'POST',
          body
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled;
          const state = getState() as any;
          const merchantCustomizations = state.rootReducer.app.merchantCustomizations;

          // Only include tip data if merchant has tip feature enabled
          const modifiedData = {
            ...data,
            invoice: {
              ...data.invoice,
              tip: merchantCustomizations?.tip?.enabled ? data.invoice?.tip : undefined,
              signatureEnabled: getIsSignatureEnabledState(merchantCustomizations?.customerSignatureConfig)
            }
          };

          // Dispatch action to populate invoice state
          dispatch(initializeInvoiceFromAutofill(modifiedData));
        } catch (error) {
          // Error handling is already covered by extraOptions
        }
      },
      extraOptions: {
        failure: 'Failed to auto-fill invoice details',
        showToast: false
      }
    }),

    //Auto-fill feedback API - used for both invoices and jobs
    submitAutoFillFeedback: builder.mutation<
      AutoFillFeedbackResponse,
      { teamId: string; body: AutoFillFeedbackPayload }
    >({
      query: ({ teamId, body }) => {
        return {
          url: `${API_VERSION.V1}/teams/${teamId}/auto-fill/feedback`,
          method: 'POST',
          body
        };
      },
      extraOptions: {
        failure: 'Failed to submit feedback',
        showToast: false
      }
    }),

    updateInvoiceReminders: builder.mutation<
      { success: boolean; message: string },
      { teamId: number; invoiceId: string; reminders: InvoiceReminder[] }
    >({
      query: ({ teamId, invoiceId, reminders }) => {
        return {
          url: `${API_VERSION.V7}/teams/${teamId}/invoices/${invoiceId}/reminders`,
          method: 'PATCH',
          body: { reminders }
        };
      },
      invalidatesTags: [API_TAG_TYPES.INVOICE_BY_ID],
      extraOptions: {
        failure: 'Failed to update invoice reminders',
        showToast: true,
        success: 'Invoice reminders updated successfully'
      }
    }),

    createInvoice: builder.mutation<
      CreateInvoiceWithRiskValidationResponse,
      { teamId: string; payload: CreateInvoiceWithRiskValidationRequest }
    >({
      query: ({ teamId, payload }) => {
        return {
          url: `${API_BASE_URL.TEAM_MANAGER}/${API_VERSION.V1}/teams/${teamId}/invoices`,
          method: 'POST',
          body: payload
        };
      },
      extraOptions: {
        failure: 'Failed to create invoice',
        showToast: false,
        success: 'Invoice created successfully'
      }
    }),

    editInvoice: builder.mutation<
      EditInvoiceWithRiskValidationResponse,
      { teamId: string; payload: EditInvoiceWithRiskValidationRequest }
    >({
      query: ({ teamId, payload }) => {
        const body =
          payload.skipWarnings !== undefined
            ? { ...payload.invoicePayload, skipWarnings: payload.skipWarnings }
            : payload.invoicePayload;
        return {
          url: `${API_BASE_URL.TEAM_MANAGER}/${API_VERSION.V1}/teams/${teamId}/invoices/${payload.invoiceId}`,
          method: 'PUT',
          body
        };
      },
      extraOptions: {
        failure: 'Failed to update invoice',
        showToast: false,
        success: 'Invoice updated successfully'
      }
    }),

    getMerchantsByTeamIdInvoice: builder.query<Merchant[], GetMerchantsPayload>({
      query: ({ teamId }) => {
        return {
          url: `${API_BASE_URL.WEB_APP}/${API_VERSION.V1}/invoices/filters/team-members?teamId=${teamId}`,
          method: 'GET'
        };
      },
      extraOptions: {
        failure: 'failed to merchant tile data'
      }
    }),

    getInvoiceAttachments: builder.query<GetInvoiceAttachmentsResponse, { teamId: number; invoiceId: string }>({
      query: ({ teamId, invoiceId }) => ({
        url: `${API_VERSION.V1}/merchants/teams/${teamId}/invoices/${invoiceId}/attachments`,
        method: 'GET'
      }),
      extraOptions: {
        failure: 'Failed to fetch invoice attachments',
        showToast: false
      }
    }),

    getInvoiceQRImage: builder.mutation<GetInvoiceQRImageResponse, GetInvoiceQRImagePayload>({
      query: ({ teamId, invoiceId, requestedAmount }) => ({
        url: `${API_VERSION.V7}/teams/${teamId}/invoices/${invoiceId}/qr`,
        method: 'POST',
        body: { requestedAmount }
      }),
      extraOptions: {
        failure: 'Failed to generate QR image',
        showToast: true
      }
    }),

    getInvoiceRefundableTransactions: builder.query<
      GetInvoiceRefundableTransactionsResponse,
      GetInvoiceRefundableTransactionsPayload
    >({
      query: params => {
        const { teamId, invoiceId } = params;
        const url = `${API_VERSION.V7}/teams/${teamId}/invoices/${invoiceId}/refundable-transactions`;
        return {
          url: url,
          method: 'GET'
        };
      },
      keepUnusedDataFor: 0,
      extraOptions: {
        failure: 'Failed to fetch invoice transactions',
        showToast: true
      }
    }),

    sendInvoiceReminder: builder.mutation<{ success: boolean; message: string }, { teamId: string; invoiceId: string }>(
      {
        query: ({ teamId, invoiceId }) => {
          return {
            url: `${API_VERSION.V7}/teams/${teamId}/invoices/${invoiceId}/send-reminder`,
            method: 'POST'
          };
        },
        invalidatesTags: [API_TAG_TYPES.INVOICE_BY_ID],
        extraOptions: {
          failure: 'Failed to send invoice reminder',
          showToast: true,
          success: 'Reminder sent successfully'
        }
      }
    ),

    closeInvoice: builder.mutation<CloseInvoiceResponse, CloseInvoicePayload>({
      query: ({ teamId, invoiceId, closureReason, closureAmount, closureNote }) => {
        return {
          url: `${API_VERSION.V7}/teams/${teamId}/invoices/${invoiceId}/close`,
          method: 'POST',
          body: {
            closureReason,
            closureAmount,
            closureNote
          }
        };
      },
      invalidatesTags: [API_TAG_TYPES.INVOICE_BY_ID, API_TAG_TYPES.INVOICES_LIST]
    })
  })
});

export const {
  useCreateInvoiceMutation,
  useEditInvoiceMutation,
  useUploadFileMutation,
  useGetPresignedUrlsMutation,
  useUpdateInvoiceRemindersMutation,
  useGetTaxesAndFeesQuery,
  useLazyDownloadInvoiceListQuery,
  useLazyDownloadPrintInvoiceAsPDFQuery,
  useLazyDownloadInvoiceAsPDFQuery,
  useLazyGetInvoicesQuery,
  useLazyGetInvoicesV2Query,
  useLazyExportInvoicesQuery,
  useLazyGetInvoiceByIdV2Query,
  useGetInvoiceByIdV2Query,
  useLazyGetCustomerByIdQuery,
  useGetInvoiceSearchSuggestionsQuery,
  useShareInvoiceMutation,
  useCreateCustomAmountTrackingMutation,
  useMarkPaidMutation,
  useDeleteInvoiceMutation,
  useLazyGetCustomersQuery,
  useLazyGetInvoicePDFPreviewQuery,
  useLazyGetMerchantsByTeamIdInvoiceQuery,
  useLazyGetAllCustomersQuery,
  useLazyGetCategoryItemListQuery,
  useGetInvoiceAttachmentsQuery,
  useLazyGetInvoiceAttachmentsQuery,
  useGetInvoiceQRImageMutation,
  useGetInvoiceRefundableTransactionsQuery,
  useLazyGetInvoiceRefundableTransactionsQuery,
  useSendInvoiceReminderMutation,
  useCloseInvoiceMutation,
  useParseInvoiceMutation,
  useSubmitAutoFillFeedbackMutation
} = InvoicesBaseApi;

export const invoicesApi = InvoicesBaseApi;
